/* eslint-disable no-param-reassign, max-lines, max-params */
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";

import { objNamesToSnakeCase } from "common/utils/helpers/cases";

const initialState = {
  index: 0,
  artworkSize: {
    width: 0,
    height: 0,
    widthInch: 0,
    heightInch: 0
  },
  editType: null
};

const artworkModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    removeBackground: (_, getters) => getters.artworkCamelCase?.removeBackground,
    artworkUrl: (state, { artworkCamelCase: artwork }) => {
      const tryToFindImageSource = () => {
        if (!artwork) {
          return URL.createObjectURL(new Blob());
        }

        if (artwork.isFile && artwork.file) {
          if (artwork?.file?.type === "application/pdf") {
            return null;
          }

          return URL.createObjectURL(artwork?.file || new Blob());
        }

        if (artwork.enhancementCompleted) {
          if (
            artwork.enhancementTier === "print_original" ||
            artwork.enhancementTier === "custom"
          ) {
            return artwork.originalImageUrl || artwork.url;
          }

          if (artwork.basicPipelineData && artwork.enhancementTier === "basic") {
            return artwork.removeBackground
              ? artwork.basicPipelineData.url
              : artwork.basicPipelineData.withBgUrl;
          }

          return artwork.removeBackground
            ? artwork.noBgUrl
            : artwork.enhancedWithBgUrl || artwork.noBgNoTrimUrl;
        }

        return artwork.originalImageUrl;
      };

      return tryToFindImageSource() || artwork?.url;
    },
    uploadArtwork: (_, getters) => Boolean(getters.artworkCamelCase?.isLoading),
    comment: (_, getters) => getters.artworkCamelCase?.enhancementNote,
    enhanced: (_, getters) => Boolean(getters.artworkCamelCase?.enhancementCompleted),
    uploadErrorText: (_, getters) => getters.artworkCamelCase?.uploadError,
    artworkSize: state => state.artworkSize,
    artworkCamelCase: state => {
      const dtfStore = useDtfStore();
      const { artworks } = dtfStore;

      return artworks[state.index];
    },
    artwork: (_, getters) => {
      if (!getters.artworkCamelCase) {
        return null;
      }

      const snakeCaseArtwork = objNamesToSnakeCase(getters.artworkCamelCase);
      snakeCaseArtwork.no_bg_url ??= snakeCaseArtwork.enhanced_no_bg_url;
      snakeCaseArtwork.with_bg_url ??= snakeCaseArtwork.enhanced_with_bg_url;

      return snakeCaseArtwork;
    },
    artworkExists: (_, getters) => !!getters.artwork && !getters.artworkCamelCase?.isSimulated,
    serializedArtwork: (_, getters) => {
      if (!getters.artwork || getters.artworkCamelCase?.isSimulated) return null;

      return {
        dtf_artwork: {
          id: getters.artwork.id,
          enhance: getters.enhanced,
          remove_background: Boolean(getters.removeBackground),
          comment: getters.comment,
          enhancement_tier: getters.artworkCamelCase.enhancementTier
        },
        width: getters.artworkSize.width,
        height: getters.artworkSize.height,
        position: ""
      };
    },
    isConnectionSetup: () => {
      const dtfStore = useDtfStore();
      return Boolean(dtfStore.dtfChannelUuid);
    },
    editType: state => state.editType
  },

  mutations: {
    setRemoveBackground(state, isRemoved) {
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({ index: state.index, removeBackground: isRemoved });
    },
    setArtworkSize(state, size) {
      state.artworkSize = size;
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({
        index: state.index,
        isSizesEdited: true,
        width: size.widthInch,
        height: size.heightInch
      });
      const artworkId = dtfStore.artworks[state.index].id;
      dtfStore.updatePreviewDpi({ artworkId });
      dtfStore.downscaleOriginalImage({ artworkId });
    },
    clearArtworkSize(state) {
      state.artworkSize = { width: null, height: null, widthInch: 0, heightInch: 0 };
    },
    setArtworkComment(state, comment) {
      const dtfStore = useDtfStore();
      dtfStore.updateArtworkValues({
        index: state.index,
        enhancementNote: (comment ?? "").trim()
      });
    },
    setEditType(state, value) {
      state.editType = value;
    },
    setMode(state, value /* front or back */) {
      if (value === "front") {
        state.index = 0;
      } else {
        state.index = 1;
      }
    }
  },

  actions: {
    /* eslint-disable max-lines-per-function */
    upload({ state }, originalFile) {
      const dtfStore = useDtfStore();
      dtfStore.addFilesToQueue({
        files: [originalFile],
        isDropEvent: false,
        artworkIndex: state.index
      });
    },
    removeArtwork({ state, getters }) {
      const dtfStore = useDtfStore();

      dtfStore.removeArtworkWithActivity(getters.artwork.id);

      // The dtf store is using filter during remove
      // When we remove the front artwork, then the back artwork will take the first place
      // So we need to add a new front artwork simulation to the front of the array
      if (state.index === 0) {
        dtfStore.$state.artworksState.unshift({
          ...dtfStore.simulation,
          index: state.index,
          id: state.index
        });
      } else if (state.index === 1) {
        dtfStore.$state.artworksState.push({
          ...dtfStore.simulation,
          index: state.index,
          id: state.index
        });
      }

      Object.assign(state, {
        ...initialState,
        index: state.index,
        id: state.index
      });

      dtfStore.getArtworksPricing();
      dtfStore.updatePdpDates();
    }
  }
};

export default artworkModule;
